import React, { memo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery } from "react-query"

import CloseIcon from "@mui/icons-material/Close"
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined"
import { Card, Grid, IconButton, Skeleton } from "@mui/material"
import { Button, type InputChangeEvent, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { SelectWithSearch } from "../../../components/form/SelectWithSearch/SelectWithSearch"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { DeploymentList } from "../../../types/generated/api/DeploymentList"
import type { PaginatedDeploymentListList } from "../../../types/generated/api/PaginatedDeploymentListList"
import type { ProgramItem } from "../../../types/generated/api/ProgramItem"
import { extractPageFromBackEndPaginationLink } from "../../../utils/genericHelpers"
import { getNonSelectedItems } from "../helpers"

import styles from "../Programs.module.scss"

interface ProjectNodeType {
  project?: { name: string; uuid: string | undefined }
  isEdit?: boolean
  menuView?: boolean
  currentIndex?: number
  selectedItems?: Array<ProgramItem>
  isProjectsLoading?: boolean
  handleRemove?: () => void
  onValueChange?: (project: { name: string; uuid: string }) => void
}

/**
 * LiveModelCard component
 * @param {boolean} isEdit
 * @param {function} handleRemove
 * @param {boolean} menuView Toggling program's "new block" mode on/off
 * @returns {React.ReactElement}
 */
export const LiveModelCard = memo(function LiveModelNode(props: ProjectNodeType): React.ReactElement {
  const { isEdit, menuView, project, currentIndex, selectedItems, isProjectsLoading, handleRemove, onValueChange } =
    props

  const readMode = isEdit ?? null

  const theme = getTheme()

  const { t } = useTranslation("common")

  const [projectsSearchValue, setProjectsSearchValue] = useState<string | undefined>(undefined)

  const {
    data: projects,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedDeploymentListList>, AxiosError, DeploymentList>(
    ["projects", "model", projectsSearchValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchProjects({
        page: pageParam,
        search: projectsSearchValue,
        category: "model",
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<DeploymentList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as DeploymentList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
    },
  )

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastMenuItemRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isFetching && !isFetchingNextPage && fetchNextPage()
    },
  })

  return (
    <Card className={menuView ? styles.LiveModelCard_menuView : styles.LiveModelCard}>
      <Grid container wrap="nowrap">
        <Grid item>
          <Grid item className={styles.LiveModelCardType}>
            <TokenOutlinedIcon sx={{ color: theme.palette.base.text[1] }} />
          </Grid>
        </Grid>

        <Grid
          item
          display={"flex"}
          flexGrow={1}
          flexDirection={"column"}
          paddingLeft={1}
          justifyContent="space-between"
          alignItems="flex-start"
          overflow={"hidden"}
        >
          <Grid item container justifyContent="space-between">
            <Grid item>
              <Typography variant="label-bold" variantColor={2}>
                {t("projectCard.liveModel")}
              </Typography>
            </Grid>
            {readMode && handleRemove && (
              <Grid item justifySelf={"end"}>
                <IconButton style={{ marginTop: "-15px" }} size="small" onClick={() => handleRemove?.()}>
                  <CloseIcon fontSize="small" className={styles.remove} />
                </IconButton>
              </Grid>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            container
            alignItems="flex-start"
            justifyContent={"space-between"}
            marginTop={0.5}
            wrap="nowrap"
          >
            <Grid item xs={9}>
              {isProjectsLoading ? (
                <Skeleton height={"25px"} width="100px" />
              ) : isEdit || menuView ? (
                <SelectWithSearch
                  menuTriggerProps={{
                    fullWidth: true,
                    hideDescription: true,
                    placeholder: t("projectCard.searchPlaceHolder"),
                    disabled: menuView || projects?.pages.length === 0,
                  }}
                  isOptionsLoading={isFetching || isFetchingNextPage}
                  lastMenuItemRef={lastMenuItemRef}
                  searchInputPlaceholder={t("searchSortFilter.search", { ns: "form" })}
                  onSelectMenuItem={({ text, value }) => onValueChange?.({ uuid: value, name: text })}
                  optionsList={
                    typeof currentIndex === "number"
                      ? getNonSelectedItems(currentIndex, projects?.pages ?? [], selectedItems ?? []).map((project) => {
                          return {
                            value: project.uuid,
                            text: project.name,
                            disabled: project.hidden,
                            item: (
                              <Typography variant="a" variantColor={1}>
                                {project.name}
                              </Typography>
                            ),
                          }
                        })
                      : []
                  }
                  inputValue={
                    project?.uuid?.startsWith("default-") ? t("projectCard.searchPlaceHolder") : (project?.name ?? "")
                  }
                  searchInputChangeHandler={(e: InputChangeEvent) => {
                    setProjectsSearchValue(e.target.value.toString())
                  }}
                  onMenuClose={() => setProjectsSearchValue(undefined)}
                />
              ) : (
                // width limit is based on
                <Typography variant="h3-bold" style={{ width: "100%", textOverflow: "ellipsis" }} noWrap>
                  {project?.name}
                </Typography>
              )}
            </Grid>

            <Grid item display={"flex"} alignSelf={"center"} justifySelf={"end"} ml={1} pb={"1px"}>
              <Button
                variant="secondary"
                /*Workaround to use window.open in safari */
                onClick={() =>
                  setTimeout(() => {
                    window.open(`/projects/${project?.uuid}/decision-engines?page=Models`, "_blank")
                  })
                }
                size="small"
                disabled={menuView || project?.uuid?.startsWith("default-")}
              >
                <LaunchOutlinedIcon
                  fontSize="medium"
                  sx={{ color: menuView ? theme.palette.gray.background[1] : theme.palette.base.text[1] }}
                />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  )
})
